import { Button, Dropdown, Flex, Icon, notify } from '@wyz/ui';
import { useModalState } from '@wyz/utils';
import { isEmpty } from 'lodash-es';
import { Link } from 'react-router';
import { Trans, useConfig } from '~/common/context';
import { ROLES } from '~/common/helpers';
import {
  useAccessControl,
  useListLocationState,
  usePricingPolicyErrorTranslation,
} from '~/common/hooks';
import { useManageQuotes } from '~/purchase/api';
import { IQuote } from '~/purchase/types';
import { useState } from 'react';
import { PdfModal } from '~/common/components';
import { QuoteRecipientModal } from '~/purchase/components';

interface QuoteActionsProps {
  quote: IQuote;
}

export const QuoteActions = ({ quote }: QuoteActionsProps) => {
  const { apiUrl, tenantCode } = useConfig();
  const acl = useAccessControl();
  const listState = useListLocationState();
  const { resolveErrorKey } = usePricingPolicyErrorTranslation();

  const manageQuotes = useManageQuotes({
    resendMail: { enabled: true },
    duplicate: { enabled: true },
  });

  const [pdf, setPdf] = useState<string | null>(null);
  const [recipientModal, recipientModalHandlers] =
    useModalState<'EDIT_RECIPIENT'>();

  const printUrl = `${apiUrl}/offers/api/tenants/${tenantCode}/customers/${quote.customerId}/quotes/${quote.reference}/print`;

  const recipient = quote.quoteEndCustomerData;
  // WCT-5218: with an address and consent on file the mail goes out straight away, once the user
  // picked whether the PDF carries the EPREL energy labels (WCT-5461); otherwise the button opens
  // the recipient modal so the data can be completed first.
  const canSendDirectly = !isEmpty(recipient.email) && recipient.gdprAccepted;
  const sendDisabled =
    !acl.isGranted([ROLES.ROLE_QUOTE_SEND_MAIL]) || quote.status === 'DRAFT';

  const sendMail = (withLabels: boolean) => {
    manageQuotes.resendMail.mutate(
      {
        customerId: quote.customerId,
        reference: quote.reference,
        withLabels,
      },
      {
        onSuccess: () => {
          notify.success(<Trans i18nKey='app.common.messages.success' />, {
            id: 'toast-success-quote-resend-mail',
          });
        },
        onError: (error) => {
          notify.error(<Trans i18nKey={resolveErrorKey(error.message)} />, {
            id: 'toast-error-quote-resend-mail',
          });
        },
      },
    );
  };

  const sendButtonContent = (withMenuCaret: boolean) => (
    <Flex className='space-x-2'>
      <Icon icon='marketing-fill' size='xxs' />
      <span>
        <Trans i18nKey='app.quote.history.table.sub_row.resend_mail' />
      </span>
      {withMenuCaret && <Icon icon='arrow-down' size='xxs' />}
    </Flex>
  );

  return (
    <Flex
      alignItems='center'
      justifyContent='center'
      className='space-x-2 '
      wrap='wrap'
    >
      {canSendDirectly && !sendDisabled ? (
        <Dropdown.Container>
          <Dropdown.Toggle
            bsPrefix='toggle'
            as='div'
            id={`resend-menu-${quote.reference}`}
          >
            <Button
              id={`resend-${quote.reference}`}
              variant='link'
              size='xs'
              isLoading={manageQuotes.resendMail.isLoading}
            >
              {sendButtonContent(true)}
            </Button>
          </Dropdown.Toggle>
          <Dropdown.Menu className='dropdown-caret dropdown-menu-card'>
            <Dropdown.Item
              id={`resend-without-labels-${quote.reference}`}
              onClick={() => sendMail(false)}
            >
              <Trans i18nKey='app.purchase.quote.history.table.sub_row.send.without_labels' />
            </Dropdown.Item>
            <Dropdown.Item
              id={`resend-with-labels-${quote.reference}`}
              onClick={() => sendMail(true)}
            >
              <Trans i18nKey='app.purchase.quote.history.table.sub_row.send.with_labels' />
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown.Container>
      ) : (
        <Button
          id={`resend-${quote.reference}`}
          variant='link'
          size='xs'
          onClick={() => recipientModalHandlers.open('EDIT_RECIPIENT')}
          disabled={sendDisabled}
        >
          {sendButtonContent(false)}
        </Button>
      )}

      <Dropdown.Container>
        <Dropdown.Toggle
          bsPrefix='toggle'
          as='div'
          id={`print-menu-${quote.reference}`}
        >
          <Button id={`print-${quote.reference}`} variant='link' size='xs'>
            <Flex className='space-x-2'>
              <Icon icon='eye' size='xxs' />
              <span>
                <Trans i18nKey='app.quote.diagnostics.table.quote.actions.pdf' />
              </span>
              <Icon icon='arrow-down' size='xxs' />
            </Flex>
          </Button>
        </Dropdown.Toggle>
        <Dropdown.Menu className='dropdown-caret dropdown-menu-card'>
          <Dropdown.Item
            id={`print-without-labels-${quote.reference}`}
            onClick={() => setPdf(printUrl)}
          >
            <Trans i18nKey='app.purchase.quote.history.table.sub_row.pdf.without_labels' />
          </Dropdown.Item>
          <Dropdown.Item
            id={`print-with-labels-${quote.reference}`}
            onClick={() => setPdf(`${printUrl}?labels=1`)}
          >
            <Trans i18nKey='app.purchase.quote.history.table.sub_row.pdf.with_labels' />
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown.Container>

      <Button
        id={`duplicate-${quote.reference}`}
        size='xs'
        variant='link'
        isLoading={manageQuotes.duplicate.isLoading}
        onClick={() => {
          manageQuotes.duplicate.mutate(
            {
              customerId: quote.customerId,
              reference: quote.reference,
            },
            {
              onSuccess: () => {
                notify.success(
                  <Trans i18nKey='app.common.messages.success' />,
                  { id: 'toast-success-quote-duplicate' },
                );
              },
              onError: (error) => {
                notify.error(
                  <Trans i18nKey={resolveErrorKey(error.message)} />,
                  { id: 'toast-error-quote-duplicate' },
                );
              },
            },
          );
        }}
        disabled={quote.status !== 'ENABLED'}
      >
        <Flex className='space-x-2'>
          <Icon icon='duplicate' size='xxs' />
          <span>
            <Trans i18nKey='app.common.button.duplicate' />
          </span>
        </Flex>
      </Button>

      {pdf !== null && <PdfModal onClose={() => setPdf(null)} url={pdf} />}

      {recipientModal === 'EDIT_RECIPIENT' && (
        <QuoteRecipientModal
          quote={quote}
          onClose={recipientModalHandlers.close}
        />
      )}

      {quote.status === 'DRAFT' && (
        <Button
          id={`edit-${quote.reference}`}
          size='xs'
          variant='link'
          as={Link}
          to={`/quotes/${quote.reference}/edit`}
          state={listState}
        >
          <Flex className='space-x-2'>
            <Icon icon='edit' size='xxs' />
            <span>
              <Trans i18nKey='app.purchase.quote.history.table.sub_row.edit' />
            </span>
          </Flex>
        </Button>
      )}
    </Flex>
  );
};
